import React, { useState } from 'react';
import { TylerState, updateReact, useTylerStateUpdate } from '../../utils/TylerState';
import {
  ForecastAccount,
  Currency,
  AccountBucket,
  AccountSide,
  CURRENCIES,
  CURRENCY_SYMBOLS,
  ACCOUNT_BUCKETS,
} from '../../types/personalForecast';
import { latestBalance } from '../../utils/forecastEngine';
import BalanceHistory from './BalanceHistory';
import EditModal, { Field, FieldGroup } from './EditModal';
import { inferAccountType } from '../../utils/forecastToRetirement';
import HistoryImport from './HistoryImport';

let nextId = 1;
function generateId(): string {
  return `acct-${Date.now()}-${nextId++}`;
}

function newBlankAccount(side: AccountSide): ForecastAccount {
  return {
    id: generateId(),
    name: '',
    currency: TylerState.forecast.settings.baseCurrency,
    bucket: 'current',
    side,
    openingBalance: 0,
    interestRate: 0,
    growthRate: 0,
    sortOrder: TylerState.forecast.accounts.length,
  };
}

const AccountsManager: React.FC = () => {
  useTylerStateUpdate();
  const accounts = TylerState.forecast.accounts;
  const viewMode = TylerState.forecast.settings.viewMode;
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);

  // The paste panel renders under the summary cards; applying a paste starts history tracking itself.
  const openImport = () => {
    setShowImport(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Interest and growth were always the same formula in the engine; the editor shows them as one rate.
  const annualRate = (acct: ForecastAccount) => (acct.interestRate || 0) + (acct.growthRate || 0);
  const setAnnualRate = (id: string, rate: number) => {
    const idx = accounts.findIndex(a => a.id === id);
    if (idx < 0) return;
    accounts[idx].growthRate = rate;
    accounts[idx].interestRate = 0;
    TylerState.forecast.isDirty = true;
    updateReact();
  };

  const updateAccount = (id: string, field: keyof ForecastAccount, value: any) => {
    const idx = accounts.findIndex(a => a.id === id);
    if (idx < 0) return;
    (accounts[idx] as any)[field] = value;
    TylerState.forecast.isDirty = true;
    updateReact();
  };

  const addAccount = (side: AccountSide) => {
    const acct = newBlankAccount(side);
    TylerState.forecast.accounts.push(acct);
    TylerState.forecast.isDirty = true;
    setEditingId(acct.id);
    updateReact();
  };

  const deleteAccount = (id: string) => {
    TylerState.forecast.accounts = accounts.filter(a => a.id !== id);
    // Also remove cash flows linked to this account
    TylerState.forecast.cashFlows = TylerState.forecast.cashFlows.filter(cf => cf.accountId !== id);
    TylerState.forecast.isDirty = true;
    updateReact();
  };

  const assets = accounts.filter(a => a.side === 'asset');
  const liabilities = accounts.filter(a => a.side === 'liability');

  const bucketTotal = (list: ForecastAccount[], bucket?: AccountBucket) => {
    const filtered = bucket ? list.filter(a => a.bucket === bucket) : list;
    return filtered.reduce((sum, a) => sum + latestBalance(a), 0);
  };

  const formatBalance = (amount: number, currency: Currency = TylerState.forecast.settings.baseCurrency) => {
    const sym = CURRENCY_SYMBOLS[currency];
    const abs = Math.abs(amount);
    const formatted = abs.toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return amount < 0 ? `-${sym}${formatted}` : `${sym}${formatted}`;
  };


  const editing = accounts.find(a => a.id === editingId);

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '5px 7px', border: '1px solid #cbd5e1', borderRadius: '4px',
  };

  const editModal = editing && (
    <EditModal
      title={`${editing.side === 'asset' ? 'Asset' : 'Debt'}: ${editing.name || '(unnamed)'}`}
      onClose={() => setEditingId(null)}
      onDelete={() => { deleteAccount(editing.id); setEditingId(null); }}
    >
      <Field label="Name" wide>
        <input type="text" value={editing.name} autoFocus style={inputStyle}
          onChange={e => updateAccount(editing.id, 'name', e.target.value)} />
      </Field>

      <Field label="Currency">
        <select value={editing.currency} style={inputStyle}
          onChange={e => updateAccount(editing.id, 'currency', e.target.value)}>
          {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>

      <Field label="Type">
        <select value={editing.bucket} style={inputStyle}
          onChange={e => updateAccount(editing.id, 'bucket', e.target.value)}>
          {ACCOUNT_BUCKETS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
        </select>
      </Field>

      <Field label="Opening balance" wide
        hint="The balance before any recorded history. Month-by-month balances go in Balance History below.">
        <input type="number" value={editing.openingBalance || ''} placeholder="0" style={inputStyle}
          onChange={e => updateAccount(editing.id, 'openingBalance', parseFloat(e.target.value) || 0)} />
      </Field>

      {editing.side === 'asset' && (
        <Field label="What kind of account is this?" wide
          hint={editing.accountType
            ? 'Used when these balances are carried to the Retirement Planner, which treats each kind completely differently.'
            : `Left on automatic, this reads as "${inferAccountType(editing)}" from the name. Set it if that is wrong - anything unclear is left out of the Retirement Planner rather than guessed at.`}>
          <select
            value={editing.accountType || ''}
            style={inputStyle}
            onChange={e => updateAccount(editing.id, 'accountType', e.target.value || undefined)}
          >
            <option value="">Automatic — work it out from the name</option>
            <option value="rrsp">RRSP / RRIF / LIRA — taxed on withdrawal</option>
            <option value="tfsa">TFSA — tax free</option>
            <option value="nonRegistered">Non-registered — taxed on gains</option>
            <option value="property">Property</option>
            <option value="other">Something else — leave it out of retirement planning</option>
          </select>
        </Field>
      )}

      {editing.side === 'asset' && (
        <Field label="Taxed when you take it out?" wide
          hint="An RRSP, a pension or CPP is taxed on withdrawal, so only part of the balance is really yours. A TFSA or a bank account is already yours. This drives Net worth (after tax).">
          <select
            value={(editing.afterTaxPercent ?? 100) >= 100 ? 'free' : 'taxed'}
            style={inputStyle}
            onChange={e => updateAccount(editing.id, 'afterTaxPercent', e.target.value === 'free' ? 100 : 65)}
          >
            <option value="free">No - already after tax (TFSA, bank, property)</option>
            <option value="taxed">Yes - taxed on withdrawal (RRSP, pension, CPP)</option>
          </select>
          {(editing.afterTaxPercent ?? 100) < 100 && (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px', fontSize: '0.78rem', color: '#64748b' }}>
              keeping
              <input
                type="number" min="0" max="100"
                value={editing.afterTaxPercent ?? 65}
                onChange={e => updateAccount(editing.id, 'afterTaxPercent', parseFloat(e.target.value) || 0)}
                style={{ width: '64px', padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: '4px', textAlign: 'right' }}
              />
              % after tax
            </div>
          )}
        </Field>
      )}

      {viewMode !== 'simple' && <>
        <FieldGroup title="How the balance moves" />
        <Field label="Annual rate %"
          hint={editing.side === 'asset'
            ? 'Growth or interest earned, compounded monthly on the opening balance.'
            : 'Interest charged on what you owe, added monthly. Leave at 0 if you model the interest as a cash flow instead - setting both charges you twice.'}>
          <input type="number" step="0.1" value={annualRate(editing) || ''} placeholder="0" style={inputStyle}
            onChange={e => setAnnualRate(editing.id, parseFloat(e.target.value) || 0)} />
        </Field>
        <Field label={editing.side === 'liability' ? 'Principal paid each month' : 'Fixed change each month'}
          hint={editing.side === 'liability'
            ? 'The balance falls by this every forecast month and stops at zero.'
            : 'A dollar change on top of growth - a revaluation, or a negative number for depreciation.'}>
          <input type="number" placeholder="0" style={inputStyle}
            value={editing.side === 'liability' ? Math.abs(editing.monthlyChange || 0) || '' : editing.monthlyChange || ''}
            onChange={e => {
              const value = parseFloat(e.target.value) || 0;
              updateAccount(editing.id, 'monthlyChange', editing.side === 'liability' ? -Math.abs(value) : value);
            }} />
        </Field>

        <FieldGroup title="Or: a percentage of other accounts" />
        <Field label="Percent" hint="Selling costs as 12% of the houses, say. Recomputed every month.">
          <input type="number" step="0.5" value={editing.linkedTo?.percent ?? ''} placeholder="%" style={inputStyle}
            onChange={e => {
              const percent = parseFloat(e.target.value);
              updateAccount(editing.id, 'linkedTo', Number.isNaN(percent)
                ? undefined : { accountIds: editing.linkedTo?.accountIds || [], percent });
            }} />
        </Field>
        <Field label="Of these accounts" hint="Hold Ctrl or Cmd to pick several.">
          <select multiple value={editing.linkedTo?.accountIds || []}
            style={{ ...inputStyle, height: '80px' }}
            onChange={e => {
              const accountIds = Array.from(e.target.selectedOptions).map(o => o.value);
              updateAccount(editing.id, 'linkedTo', { accountIds, percent: editing.linkedTo?.percent ?? 0 });
            }}>
            {accounts.filter(a => a.id !== editing.id && !a.linkedTo)
              .map(a => <option key={a.id} value={a.id}>{a.name || '(unnamed)'}</option>)}
          </select>
        </Field>
      </>}
    </EditModal>
  );

  const renderAccountRow = (acct: ForecastAccount) => {
    return (
      <tr
        key={acct.id}
        className="account-row"
        onClick={() => setEditingId(acct.id)}
      >
        <td className="name-cell" title={acct.name}>
          {acct.name || '(unnamed)'}
          {acct.linkedTo && acct.linkedTo.accountIds.length > 0 && (
            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
              = {acct.linkedTo.percent}% of {acct.linkedTo.accountIds.map(id => accounts.find(a => a.id === id)?.name || '?').join(' + ')}
            </div>
          )}
        </td>
        <td>{acct.currency}</td>
        <td>{ACCOUNT_BUCKETS.find(b => b.value === acct.bucket)?.label}</td>
        <td style={{ textAlign: 'right', fontWeight: 600, color: acct.side === 'asset' ? '#059669' : '#dc2626' }} title={acct.actuals && Object.keys(acct.actuals).length ? 'Latest recorded balance from Balance History' : 'Opening balance'}>
          {formatBalance(latestBalance(acct), acct.currency)}
        </td>
        {viewMode !== 'simple' && (
          <>
            <td style={{ textAlign: 'right' }}>{annualRate(acct) ? `${annualRate(acct)}%` : '-'}</td>
            <td style={{ textAlign: 'right' }}>
              {acct.monthlyChange ? formatBalance(acct.side === 'liability' ? Math.abs(acct.monthlyChange) : acct.monthlyChange, acct.currency) : '-'}
            </td>
            <td style={{ textAlign: 'right' }}>{acct.linkedTo?.percent ? `${acct.linkedTo.percent}%` : '-'}</td>
          </>
        )}
        <td>
          <button
            onClick={e => { e.stopPropagation(); deleteAccount(acct.id); }}
            style={{ padding: '2px 8px', background: 'none', color: '#94a3b8', border: 'none', cursor: 'pointer', fontSize: '1.1rem' }}
            title="Delete account"
          >
            x
          </button>
        </td>
      </tr>
    );
  };

  const renderTable = (title: string, list: ForecastAccount[], side: AccountSide) => (
    <div className="account-section" style={{ marginBottom: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ margin: 0 }}>{title}</h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          {side === 'asset' && (
            <button className="btn-primary" onClick={openImport} style={{ padding: '6px 16px', fontSize: '0.9rem' }}>
              Paste from spreadsheet
            </button>
          )}
          <button
            className="btn-secondary"
            onClick={() => addAccount(side)}
            style={{ padding: '6px 16px', fontSize: '0.9rem' }}
          >
            + Add {side === 'asset' ? 'Asset' : 'Liability'}
          </button>
        </div>
      </div>

      {list.length === 0 ? (
        <p style={{ color: '#94a3b8', fontStyle: 'italic', padding: '1rem 0' }}>
          {side === 'asset'
            ? 'No assets yet. Paste rows from your spreadsheet, or click "Add" to create one.'
            : 'No liabilities yet. Click "Add" to create one.'}
        </p>
      ) : (
        <div className="frozen-table-wrap">
          <table className="accounts-table compact frozen-table sticky-actions">
            <thead>
              <tr>
                <th>Name</th>
                <th>Currency</th>
                <th>Bucket</th>
                <th style={{ textAlign: 'right' }}>Balance</th>
                {viewMode !== 'simple' && (
                  <>
                    <th style={{ textAlign: 'right' }} title="Annual rate, compounded monthly on the opening balance">Rate %</th>
                    <th style={{ textAlign: 'right' }} title={side === 'liability' ? 'Principal paid each month' : 'Fixed dollar change each month'}>
                      {side === 'liability' ? 'Payment / mo' : 'Change / mo'}
                    </th>
                    <th style={{ textAlign: 'right' }} title="Balance as a % of other accounts">Linked</th>
                  </>
                )}
                <th style={{ width: '100px' }}></th>
              </tr>
            </thead>
            <tbody>
              {list.map(renderAccountRow)}
              <tr className="total-row">
                <td colSpan={3}><strong>Total</strong></td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>
                  {formatBalance(bucketTotal(list))}
                </td>
                {viewMode !== 'simple' && <><td /><td /><td /></>}
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Bucket subtotals in advanced mode */}
      {viewMode !== 'simple' && list.length > 0 && (
        <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.75rem', fontSize: '0.9rem', color: '#64748b' }}>
          {ACCOUNT_BUCKETS.map(b => {
            const total = bucketTotal(list, b.value);
            if (total === 0 && list.filter(a => a.bucket === b.value).length === 0) return null;
            return (
              <span key={b.value}>
                {b.label}: <strong>{formatBalance(total)}</strong>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );

  const totalAssets = bucketTotal(assets);
  const totalLiabilities = bucketTotal(liabilities);
  const netWorth = totalAssets - Math.abs(totalLiabilities);

  return (
    <div>
      {editModal}
      {/* Net worth summary */}
      <div className="summary-cards" style={{ marginBottom: '2rem' }}>
        <div className="summary-card positive">
          <div className="card-label">Total Assets</div>
          <div className="card-value">{formatBalance(totalAssets)}</div>
        </div>
        <div className="summary-card negative">
          <div className="card-label">Total Liabilities</div>
          <div className="card-value">{formatBalance(totalLiabilities)}</div>
        </div>
        <div className={`summary-card ${netWorth >= 0 ? 'positive' : 'negative'}`}>
          <div className="card-label">Net Worth</div>
          <div className="card-value">{formatBalance(netWorth)}</div>
        </div>
      </div>

      {showImport && <HistoryImport onClose={() => setShowImport(false)} />}

      {renderTable('Assets (What You Own)', assets, 'asset')}
      {renderTable('Liabilities (What You Owe)', liabilities, 'liability')}
      <BalanceHistory onPaste={openImport} />
    </div>
  );
};

export default AccountsManager;
